import { useState } from 'react';
import { Member, TeamName, useScore } from '../../../context/ScoreContext';
import { useComments } from '../../../hooks/useComments';

type FilterTeam = 'all' | 'black' | 'white';

function MemberRow({
  member,
  onSwitchTeam,
  onRemove,
  onTransfer,
}: {
  member: Member;
  onSwitchTeam: (m: Member) => void;
  onRemove: (m: Member) => void;
  onTransfer: (m: Member) => void;
}) {
  const isBlack = member.team === 'black';
  const accentColor = isBlack ? '#d4a843' : '#e0e0e0';

  return (
    <div
      className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border transition-all"
      style={{ background: '#0a0a0a', borderColor: '#141414' }}
    >
      {/* Team bar */}
      <div className="w-0.5 h-8 rounded-full shrink-0" style={{ background: accentColor + '50' }} />

      {/* Avatar */}
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black shrink-0"
        style={{ background: `${accentColor}15`, color: accentColor, border: `1px solid ${accentColor}30` }}
      >
        {member.name.charAt(0).toUpperCase()}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-white text-sm font-bold truncate">{member.name}</span>
          <span
            className="text-[9px] px-1.5 py-0.5 rounded-full font-bold shrink-0 font-cinzel"
            style={{ background: `${accentColor}18`, color: accentColor }}
          >
            {isBlack ? 'B' : 'W'}
          </span>
        </div>
        {member.goal
          ? <p className="text-[#333] text-[11px] truncate"><i className="ri-flag-2-line mr-1" style={{ color: `${accentColor}40` }} />{member.goal}</p>
          : <p className="text-[#252525] text-[11px] italic">未設定目標</p>
        }
      </div>

      {/* Transfer record */}
      <button
        onClick={() => onTransfer(member)}
        title="繼承紀錄"
        className="w-8 h-8 flex items-center justify-center rounded-xl cursor-pointer transition-all shrink-0 text-[#2a2a2a] hover:text-[#7ec8a0] hover:bg-[#7ec8a0]/08 border border-transparent hover:border-[#7ec8a0]/20"
      >
        <i className="ri-file-transfer-line text-sm" />
      </button>

      {/* Switch team */}
      <button
        onClick={() => onSwitchTeam(member)}
        title={`移至${isBlack ? '白隊' : '黑隊'}`}
        className="w-8 h-8 flex items-center justify-center rounded-xl cursor-pointer transition-all shrink-0 text-[#2a2a2a] hover:text-[#d4a843] hover:bg-[#d4a843]/08 border border-transparent hover:border-[#d4a843]/20"
      >
        <i className="ri-swap-line text-sm" />
      </button>

      {/* Remove member */}
      <button
        onClick={() => onRemove(member)}
        title="移除成員"
        className="w-8 h-8 flex items-center justify-center rounded-xl cursor-pointer transition-all shrink-0 text-[#2a2a2a] hover:text-[#e05555] hover:bg-[#e05555]/08 border border-transparent hover:border-[#e05555]/20"
      >
        <i className="ri-user-unfollow-line text-sm" />
      </button>
    </div>
  );
}

// ── Remove Confirm Dialog ─────────────────────────────────────────────────────
function RemoveConfirmDialog({
  member,
  onConfirm,
  onCancel,
}: {
  member: Member;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} />
      <div
        className="relative z-10 w-full max-w-xs rounded-2xl border overflow-hidden"
        style={{ background: '#0d0d0d', borderColor: '#e0555530' }}
      >
        <div className="px-5 pt-5 pb-4 border-b border-[#1a1a1a]">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 flex items-center justify-center rounded-full shrink-0"
              style={{ background: '#e0555518', border: '1px solid #e0555530' }}
            >
              <i className="ri-user-unfollow-line text-base" style={{ color: '#e05555' }} />
            </div>
            <div>
              <p className="text-white text-sm font-bold">移除成員</p>
              <p className="text-[#555] text-[11px]">此動作無法復原</p>
            </div>
          </div>
        </div>
        <div className="px-5 py-4">
          <p className="text-[#888] text-sm leading-relaxed">
            確定要將{' '}
            <span className="text-white font-bold">「{member.name}」</span>{' '}
            從{member.team === 'black' ? '黑隊' : '白隊'}移除？
          </p>
          <p className="text-[#444] text-xs mt-1.5">移除後該成員需重新加入才能繼續參與遊戲。</p>
        </div>
        <div className="px-5 pb-5 flex gap-2">
          <button
            onClick={onCancel}
            className="flex-1 py-2.5 rounded-xl text-xs border border-[#1e1e1e] text-[#555] cursor-pointer hover:text-[#888] transition-colors whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-2.5 rounded-xl text-xs font-bold cursor-pointer transition-colors whitespace-nowrap"
            style={{ background: '#e05555', color: '#fff' }}
          >
            確認移除
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Transfer Record Dialog ────────────────────────────────────────────────────
function TransferRecordDialog({
  fromMember,
  members,
  scores,
  onConfirm,
  onCancel,
}: {
  fromMember: Member;
  members: Member[];
  scores: Record<string, number>;
  onConfirm: (toMemberId: string) => void;
  onCancel: () => void;
}) {
  const [selectedId, setSelectedId] = useState<string>('');
  const [step, setStep] = useState<'select' | 'confirm'>('select');

  const fromScore = scores[fromMember.id] ?? 0;
  const toMember = members.find(m => m.id === selectedId);
  const toScore = toMember ? (scores[toMember.id] ?? 0) : 0;

  // 排除自己
  const candidates = members.filter(m => m.id !== fromMember.id);

  const handleNext = () => {
    if (!selectedId) return;
    setStep('confirm');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onCancel} />
      <div
        className="relative z-10 w-full max-w-sm rounded-2xl border overflow-hidden"
        style={{ background: '#0d0d0d', borderColor: '#7ec8a030' }}
      >
        {/* Header */}
        <div className="px-5 pt-5 pb-4 border-b border-[#1a1a1a]">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 flex items-center justify-center rounded-full shrink-0"
              style={{ background: '#7ec8a018', border: '1px solid #7ec8a030' }}
            >
              <i className="ri-file-transfer-line text-base" style={{ color: '#7ec8a0' }} />
            </div>
            <div>
              <p className="text-white text-sm font-bold">繼承紀錄</p>
              <p className="text-[#555] text-[11px]">將成員的積分與歷史移轉給另一位成員</p>
            </div>
          </div>
        </div>

        {step === 'select' ? (
          <>
            {/* From member info */}
            <div className="px-5 pt-4 pb-3">
              <p className="text-[#444] text-[10px] tracking-[0.3em] uppercase mb-2">來源成員</p>
              <div
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl border"
                style={{
                  background: fromMember.team === 'black' ? '#d4a84310' : '#e0e0e010',
                  borderColor: fromMember.team === 'black' ? '#d4a84325' : '#e0e0e025',
                }}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-black shrink-0"
                  style={{
                    background: fromMember.team === 'black' ? '#d4a84320' : '#e0e0e020',
                    color: fromMember.team === 'black' ? '#d4a843' : '#e0e0e0',
                  }}
                >
                  {fromMember.name.charAt(0).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-bold truncate">{fromMember.name}</p>
                  <p className="text-[#555] text-[11px]">{fromMember.team === 'black' ? '黑隊' : '白隊'} · 累計 {fromScore} 分</p>
                </div>
                <i className="ri-arrow-right-line text-[#7ec8a0] text-sm" />
              </div>
            </div>

            {/* Target member select */}
            <div className="px-5 pb-4">
              <p className="text-[#444] text-[10px] tracking-[0.3em] uppercase mb-2">繼承目標</p>
              {candidates.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-6 gap-2 text-[#333]">
                  <i className="ri-user-search-line text-xl" />
                  <p className="text-xs">沒有其他成員可選</p>
                </div>
              ) : (
                <div className="space-y-1.5 max-h-48 overflow-y-auto">
                  {candidates.map(m => {
                    const isSelected = selectedId === m.id;
                    const mScore = scores[m.id] ?? 0;
                    const mColor = m.team === 'black' ? '#d4a843' : '#e0e0e0';
                    return (
                      <button
                        key={m.id}
                        onClick={() => setSelectedId(m.id)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-all text-left"
                        style={isSelected
                          ? { background: '#7ec8a015', borderColor: '#7ec8a040' }
                          : { background: '#0a0a0a', borderColor: '#141414' }
                        }
                      >
                        <div
                          className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-black shrink-0"
                          style={{ background: `${mColor}15`, color: mColor }}
                        >
                          {m.name.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-bold truncate">{m.name}</p>
                          <p className="text-[11px]" style={{ color: `${mColor}80` }}>
                            {m.team === 'black' ? '黑隊' : '白隊'} · 目前 {mScore} 分
                          </p>
                        </div>
                        {isSelected && (
                          <div className="w-5 h-5 flex items-center justify-center rounded-full shrink-0" style={{ background: '#7ec8a0' }}>
                            <i className="ri-check-line text-[10px] text-black" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="px-5 pb-5 flex gap-2">
              <button
                onClick={onCancel}
                className="flex-1 py-2.5 rounded-xl text-xs border border-[#1e1e1e] text-[#555] cursor-pointer hover:text-[#888] transition-colors whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleNext}
                disabled={!selectedId}
                className="flex-1 py-2.5 rounded-xl text-xs font-bold cursor-pointer transition-all whitespace-nowrap disabled:opacity-30 disabled:cursor-not-allowed"
                style={{ background: selectedId ? '#7ec8a0' : '#333', color: selectedId ? '#0d0d0d' : '#555' }}
              >
                下一步
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Confirm step */}
            <div className="px-5 py-4">
              <p className="text-[#888] text-sm leading-relaxed mb-4">
                確認將{' '}
                <span className="text-white font-bold">「{fromMember.name}」</span>{' '}
                的所有積分紀錄繼承給{' '}
                <span className="text-white font-bold">「{toMember?.name}」</span>？
              </p>

              {/* Score preview */}
              <div
                className="rounded-xl border p-3.5 space-y-2.5"
                style={{ background: '#0a0a0a', borderColor: '#7ec8a020' }}
              >
                <p className="text-[#444] text-[10px] tracking-[0.3em] uppercase">積分變化預覽</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 text-center">
                    <p className="text-[#555] text-[10px] mb-1">{fromMember.name}</p>
                    <p className="text-white text-lg font-black font-orbitron">{fromScore}</p>
                    <p className="text-[#e05555] text-[10px] mt-0.5">→ 0</p>
                  </div>
                  <div className="flex flex-col items-center gap-1">
                    <i className="ri-arrow-right-line text-[#7ec8a0] text-base" />
                    <span className="text-[#7ec8a0] text-[10px] font-bold">+{fromScore}</span>
                  </div>
                  <div className="flex-1 text-center">
                    <p className="text-[#555] text-[10px] mb-1">{toMember?.name}</p>
                    <p className="text-white text-lg font-black font-orbitron">{toScore}</p>
                    <p className="text-[#7ec8a0] text-[10px] mt-0.5">→ {toScore + fromScore}</p>
                  </div>
                </div>
              </div>

              <p className="text-[#333] text-[11px] mt-3 leading-relaxed">
                歷史紀錄中屬於「{fromMember.name}」的項目也會同步更新為「{toMember?.name}」。此動作無法復原。
              </p>
            </div>

            <div className="px-5 pb-5 flex gap-2">
              <button
                onClick={() => setStep('select')}
                className="flex-1 py-2.5 rounded-xl text-xs border border-[#1e1e1e] text-[#555] cursor-pointer hover:text-[#888] transition-colors whitespace-nowrap"
              >
                返回
              </button>
              <button
                onClick={() => onConfirm(selectedId)}
                className="flex-1 py-2.5 rounded-xl text-xs font-bold cursor-pointer transition-colors whitespace-nowrap"
                style={{ background: '#7ec8a0', color: '#0d0d0d' }}
              >
                確認繼承
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Main Panel ────────────────────────────────────────────────────────────────
export default function MemberManagementPanel() {
  const { members, scores, updateMemberTeam, removeMember, transferMemberRecord, teamMottos, updateTeamMotto } = useScore();
  const { comments, clearAllComments } = useComments();
  const [filterTeam, setFilterTeam] = useState<FilterTeam>('all');
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<Member | null>(null);
  const [transferTarget, setTransferTarget] = useState<Member | null>(null);
  const [feedback, setFeedback] = useState<string | null>(null);
  const [editingMotto, setEditingMotto] = useState<TeamName | null>(null);
  const [mottoDraft, setMottoDraft] = useState('');

  const blackMembers = members.filter(m => m.team === 'black');
  const whiteMembers = members.filter(m => m.team === 'white');
  const filteredMembers = filterTeam === 'all' ? members : members.filter(m => m.team === filterTeam);

  const showFeedback = (msg: string) => {
    setFeedback(msg);
    setTimeout(() => setFeedback(null), 2500);
  };

  const handleEditMotto = (team: TeamName) => {
    setMottoDraft(teamMottos[team]);
    setEditingMotto(team);
  };

  const handleSaveMotto = () => {
    if (!editingMotto) return;
    updateTeamMotto(editingMotto, mottoDraft.trim());
    setEditingMotto(null);
    showFeedback('✓ 口號已更新');
  };

  const handleClearComments = async () => {
    await clearAllComments();
    setShowClearConfirm(false);
    showFeedback('✓ 所有留言已清除');
  };

  const handleSwitchTeam = async (member: Member) => {
    const newTeam: TeamName = member.team === 'black' ? 'white' : 'black';
    await updateMemberTeam(member.id, newTeam);
    showFeedback(`✓ 「${member.name}」已移至${newTeam === 'black' ? '黑隊' : '白隊'}`);
  };

  const handleRemoveConfirm = async () => {
    if (!removeTarget) return;
    const name = removeTarget.name;
    await removeMember(removeTarget.id);
    setRemoveTarget(null);
    showFeedback(`✓ 「${name}」已移除`);
  };

  const handleTransferConfirm = async (toMemberId: string) => {
    if (!transferTarget) return;
    const fromName = transferTarget.name;
    const toMember = members.find(m => m.id === toMemberId);
    try {
      await transferMemberRecord(transferTarget.id, toMemberId);
      setTransferTarget(null);
      showFeedback(`✓ 「${fromName}」的紀錄已繼承給「${toMember?.name ?? ''}」`);
    } catch {
      showFeedback('⚠ 繼承失敗，請重試');
    }
  };

  return (
    <div className="flex-1 px-5 py-5 space-y-5">

      {/* Remove confirm dialog */}
      {removeTarget && (
        <RemoveConfirmDialog
          member={removeTarget}
          onConfirm={handleRemoveConfirm}
          onCancel={() => setRemoveTarget(null)}
        />
      )}

      {/* Transfer record dialog */}
      {transferTarget && (
        <TransferRecordDialog
          fromMember={transferTarget}
          members={members}
          scores={scores.memberScores}
          onConfirm={handleTransferConfirm}
          onCancel={() => setTransferTarget(null)}
        />
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: '黑隊', value: blackMembers.length, color: '#d4a843', icon: 'ri-shield-fill' },
          { label: '總人數', value: members.length, color: '#888', icon: 'ri-group-fill' },
          { label: '白隊', value: whiteMembers.length, color: '#e0e0e0', icon: 'ri-sword-fill' },
        ].map(s => (
          <div
            key={s.label}
            className="rounded-xl p-3 text-center border"
            style={{ background: `${s.color}06`, borderColor: `${s.color}18` }}
          >
            <i className={`${s.icon} text-sm mb-1 block`} style={{ color: `${s.color}80` }} />
            <div className="text-white text-xl font-black font-orbitron">{s.value}</div>
            <div className="text-[10px] tracking-widest mt-0.5 font-cinzel" style={{ color: `${s.color}50` }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter */}
      <div>
        <p className="text-[#444] text-[10px] tracking-[0.35em] uppercase mb-2">篩選隊伍</p>
        <div className="flex gap-1.5 p-1 bg-[#0d0d0d] border border-[#181818] rounded-xl">
          {([
            { key: 'all', label: '全部', color: '#888' },
            { key: 'black', label: '黑隊', color: '#d4a843' },
            { key: 'white', label: '白隊', color: '#e0e0e0' },
          ] as { key: FilterTeam; label: string; color: string }[]).map(opt => (
            <button
              key={opt.key}
              onClick={() => setFilterTeam(opt.key)}
              className="flex-1 py-2 rounded-lg text-xs font-bold cursor-pointer transition-all whitespace-nowrap"
              style={filterTeam === opt.key
                ? { background: `${opt.color}15`, color: opt.color, border: `1px solid ${opt.color}25` }
                : { color: '#2a2a2a', border: '1px solid transparent' }
              }
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Member list */}
      <div>
        <div className="flex items-center justify-between mb-2.5">
          <p className="text-[#444] text-[10px] tracking-[0.35em] uppercase">成員名單</p>
          <span className="text-[#252525] text-[10px]">{filteredMembers.length} 人</span>
        </div>
        {filteredMembers.length === 0
          ? (
            <div className="flex flex-col items-center justify-center py-8 gap-2 text-[#222]">
              <i className="ri-user-search-line text-2xl" />
              <p className="text-xs">尚無成員</p>
            </div>
          )
          : (
            <div className="space-y-1.5">
              {filteredMembers.map(m => (
                <MemberRow
                  key={m.id}
                  member={m}
                  onSwitchTeam={handleSwitchTeam}
                  onRemove={setRemoveTarget}
                  onTransfer={setTransferTarget}
                />
              ))}
            </div>
          )
        }
      </div>

      {/* Team Mottos */}
      <div className="pt-1 border-t border-[#0f0f0f]">
        <p className="text-[#444] text-[10px] tracking-[0.35em] uppercase mb-3">戰隊口號</p>
        <div className="space-y-2">
          {(['black', 'white'] as TeamName[]).map(team => {
            const isBlack = team === 'black';
            const color = isBlack ? '#d4a843' : '#e0e0e0';
            const motto = teamMottos[team];
            const isEditing = editingMotto === team;

            return (
              <div
                key={team}
                className="rounded-xl border p-3.5"
                style={{ borderColor: `${color}18`, background: `${color}05` }}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className="w-6 h-6 rounded-lg flex items-center justify-center"
                    style={{ background: `${color}15`, border: `1px solid ${color}25` }}
                  >
                    <i className={`${isBlack ? 'ri-shield-fill' : 'ri-sword-fill'} text-[10px]`} style={{ color }} />
                  </div>
                  <span className="text-xs font-bold tracking-wider font-cinzel" style={{ color }}>
                    {isBlack ? 'BLACK' : 'WHITE'} TEAM
                  </span>
                  {!isEditing && (
                    <button
                      onClick={() => handleEditMotto(team)}
                      className="ml-auto flex items-center gap-1 text-[10px] text-[#333] hover:text-[#888] cursor-pointer transition-colors py-0.5 px-1.5 rounded-lg hover:bg-[#1a1a1a]"
                    >
                      <i className="ri-pencil-line" />編輯
                    </button>
                  )}
                </div>

                {isEditing ? (
                  <div className="space-y-2">
                    <input
                      type="text"
                      value={mottoDraft}
                      onChange={e => setMottoDraft(e.target.value)}
                      maxLength={40}
                      placeholder="輸入戰隊口號…"
                      className="w-full bg-[#0d0d0d] border rounded-xl px-3 py-2 text-white text-xs placeholder-[#333] outline-none"
                      style={{ borderColor: `${color}30` }}
                      autoFocus
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleSaveMotto}
                        className="flex-1 py-2 rounded-xl text-xs font-bold cursor-pointer whitespace-nowrap"
                        style={{ background: color, color: '#0d0d0d' }}
                      >
                        儲存
                      </button>
                      <button
                        onClick={() => setEditingMotto(null)}
                        className="flex-1 py-2 rounded-xl text-xs border border-[#1e1e1e] text-[#555] cursor-pointer whitespace-nowrap"
                      >
                        取消
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs font-bold italic tracking-wide" style={{ color: motto ? color : '#2a2a2a' }}>
                    {motto || '尚未設定口號'}
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Danger zone */}
      <div className="pt-1 border-t border-[#0f0f0f]">
        <p className="text-[#444] text-[10px] tracking-[0.35em] uppercase mb-3">危險操作</p>
        {showClearConfirm ? (
          <div className="bg-[#0a0a0a] border border-[#e05555]/15 rounded-xl p-3.5">
            <p className="text-[#666] text-xs mb-3 leading-relaxed">
              確定清除全部 <span className="text-[#e05555] font-bold">{comments.length}</span> 則留言？此動作無法復原。
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setShowClearConfirm(false)}
                className="flex-1 border border-[#1e1e1e] text-[#555] rounded-xl py-2 text-xs cursor-pointer hover:text-[#888] transition-colors whitespace-nowrap"
              >
                取消
              </button>
              <button
                onClick={handleClearComments}
                className="flex-1 bg-[#e05555] text-white rounded-xl py-2 text-xs font-bold cursor-pointer hover:bg-[#c94444] transition-colors whitespace-nowrap"
              >
                確認清除
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowClearConfirm(true)}
            className="flex items-center gap-1.5 text-[#e05555]/30 hover:text-[#e05555]/70 text-xs cursor-pointer transition-colors"
          >
            <i className="ri-delete-bin-line" />清除全部留言（{comments.length} 則）
          </button>
        )}
      </div>

      {/* Feedback */}
      {feedback && (
        <div className="flex items-center justify-center gap-2 py-2.5 rounded-xl border border-[#7ec8a0]/20 bg-[#7ec8a0]/05">
          <i className="ri-checkbox-circle-line text-[#7ec8a0]" />
          <span className="text-[#7ec8a0] text-sm font-bold">{feedback}</span>
        </div>
      )}
    </div>
  );
}
